import { type ComponentProps, memo } from 'react';

import { cn } from '@air/utils-tailwind';

import { usePlayerStore } from './PlayerContextProvider';

const PERCENTAGE_SCALE = 100;

export type PlayerSeekbarChaptersProps = Pick<ComponentProps<'div'>, 'className'> & {
  chapters: {
    type: 'chapter' | 'gap';
    title: string;
    startTime: number;
    endTime: number;
    summary: string;
  }[];
};

export const PlayerSeekbarChapters = memo(({ chapters, className }: PlayerSeekbarChaptersProps) => {
  const currentTime = usePlayerStore((state) => state.currentTime);
  const duration = usePlayerStore((state) => state.duration);

  return (
    <div className={cn('relative flex w-full gap-0.5', className)} data-testid="PLAYER_SEEKBAR_CHAPTERS">
      {chapters.map((chapter) => {
        const chapterPercentage =
          ((currentTime - chapter.startTime) / (chapter.endTime - chapter.startTime)) * PERCENTAGE_SCALE;
        let progressPercentage = chapterPercentage;

        if (Number.isNaN(chapterPercentage)) {
          progressPercentage = 0;
        } else if (currentTime >= chapter.endTime) {
          progressPercentage = PERCENTAGE_SCALE;
        }

        return (
          <div
            className="h-1 overflow-hidden rounded ads:bg-input bg-gray-7"
            data-end-time={chapter.endTime}
            data-start-time={chapter.startTime}
            key={chapter.startTime}
            style={{
              width: `${((chapter.endTime - chapter.startTime) / duration) * PERCENTAGE_SCALE}%`,
            }}
          >
            <div
              className="size-full rounded-4 ads:bg-primary bg-blue-9"
              style={{ transform: `translateX(-${PERCENTAGE_SCALE - progressPercentage}%)` }}
            />
          </div>
        );
      })}
    </div>
  );
});

PlayerSeekbarChapters.displayName = 'PlayerSeekbarChapters';
